// src/WOHistoryPanel.js
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api";

export default function WOHistoryPanel({ site, woNumber }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // filters (like your desktop)
  const [fDate, setFDate] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [fAction, setFAction] = useState("");
  const [fNotes, setFNotes] = useState("");
  const [fWho, setFWho] = useState("");

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const qs = new URLSearchParams({ site, wo_number: woNumber });
      const r = await apiFetch(`/api/wo/history?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setRows((await r.json()) || []);
    } catch (e) {
      setErr(String(e.message || e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!site || !woNumber) return;
    load();
  }, [site, woNumber]);

  const filtered = useMemo(() => {
    const d = fDate.trim();
    const desc = fDesc.trim().toLowerCase();
    const act = fAction.trim().toLowerCase();
    const notes = fNotes.trim().toLowerCase();
    const who = fWho.trim().toLowerCase();

    return rows.filter((r) => {
      const created = String(r.created_at || r.uploaded_at || "");
      const description = String(r.description || "");
      const action = String(r.action || "");
      const n = String(r.notes || "");
      const created_by = String(r.created_by || "");

      if (d && !created.includes(d)) return false;
      if (desc && !description.toLowerCase().includes(desc)) return false;
      if (act && !action.toLowerCase().includes(act)) return false;
      if (notes && !n.toLowerCase().includes(notes)) return false;
      if (who && !created_by.toLowerCase().includes(who)) return false;
      return true;
    });
  }, [rows, fDate, fDesc, fAction, fNotes, fWho]);

  const reset = () => {
    setFDate("");
    setFDesc("");
    setFAction("");
    setFNotes("");
    setFWho("");
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className="font-semibold">WO History</div>
        <button
          className="ml-auto px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          type="button"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
        <button
          className="px-3 py-2 rounded bg-orange-500 text-white hover:bg-orange-600"
          type="button"
          onClick={reset}
        >
          Reset Filters
        </button>
      </div>

      {err && <p className="text-red-600 mb-2">{err}</p>}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
        <input className="border rounded px-2 py-2" placeholder="Date/Time" value={fDate} onChange={(e) => setFDate(e.target.value)} />
        <input className="border rounded px-2 py-2" placeholder="Description" value={fDesc} onChange={(e) => setFDesc(e.target.value)} />
        <input className="border rounded px-2 py-2" placeholder="Action" value={fAction} onChange={(e) => setFAction(e.target.value)} />
        <input className="border rounded px-2 py-2" placeholder="Notes" value={fNotes} onChange={(e) => setFNotes(e.target.value)} />
        <input className="border rounded px-2 py-2" placeholder="Created By" value={fWho} onChange={(e) => setFWho(e.target.value)} />
      </div>

      <div className="border rounded overflow-auto max-h-[520px]">
        {filtered.map((r, idx) => (
          <div key={r.id ?? idx} className={`px-3 py-2 border-b ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
            <div className="text-xs text-gray-500">{String(r.created_at || "")}</div>
            <div className="text-sm font-semibold">{r.action || "-"}</div>
            <div className="text-sm text-gray-700">{r.description || ""}</div>
            {r.notes ? <div className="text-xs text-gray-600 mt-1">{r.notes}</div> : null}
            <div className="text-xs text-gray-500 mt-1">By: {r.created_by || "-"}</div>
          </div>
        ))}
        {loading && <div className="p-3 text-sm text-gray-600">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="p-3 text-sm text-gray-600">No history entries found.</div>}
      </div>
    </div>
  );
}
